/**
 * Utilitários para adaptar dados de tickets para visualização Kanban
 */

import type { TicketKanbanBoardAggregates, TicketPipelineResponse } from '@troia-v3/types';
import type { PopulatedTicket, TicketsMetrics } from '../types';

// ============================================================================
// SCOPE HELPERS (post D-context-gate, 2026-04-24)
// Espelham `leadsAdapter.getEffectiveScopeForFunnels` / `mapScopeToDropdownFlag`.
// ============================================================================

export type TicketsScope = 'own' | 'team' | 'all';

/**
 * Resolve o scope efetivo do user logado a partir de uma lista de pipelines
 * (com `userScope` hidratado pelo backend a partir de `ticket-pipelines-users`).
 * Pega o mais permissivo: 'all' > 'team' > 'own'. Usado em listagens
 * cross-pipeline; em pages per-pipeline usar `pipeline.userScope` direto.
 */
export function getEffectiveScopeForPipelines(
  pipelines: Pick<TicketPipelineResponse, 'userScope'>[],
): TicketsScope {
  if (pipelines.some((p) => p.userScope === 'all')) return 'all';
  if (pipelines.some((p) => p.userScope === 'team')) return 'team';
  return 'own';
}

/**
 * Mapeia scope efetivo → flag passada ao `useUsersDropdown`:
 * - `'all'`  → `'all'`
 * - `'team'` → `'team'`
 * - `'own'`  → `undefined` (filtro não exibido)
 */
export function mapScopeToDropdownFlag(scope: TicketsScope): 'team' | 'all' | undefined {
  if (scope === 'all') return 'all';
  if (scope === 'team') return 'team';
  return undefined;
}

/**
 * Métricas do header/sidebar a partir dos agregados do board (modo kanban,
 * SP3) — mesmo shape do `calculateTicketsMetrics` (que permanece para a
 * table view). Tempos médios não existem nos aggregates do backend — e
 * contar só as janelas carregadas seria enganoso — então ficam zerados no
 * modo kanban (espelho do `metricsFromAggregates` do leadsAdapter).
 */
export function metricsFromAggregates(aggregates: TicketKanbanBoardAggregates): TicketsMetrics {
  const pc = aggregates.priorityCounts;
  return {
    total: aggregates.total,
    byCategory: { ...aggregates.statusCategoryCounts },
    slaBreached: aggregates.slaBreachedCount,
    avgResponseTime: 0,
    avgResolutionTime: 0,
    priorityCounts: {
      low: pc.low ?? 0,
      medium: pc.medium ?? 0,
      high: pc.high ?? 0,
      urgent: pc.urgent ?? 0,
    },
  };
}

/**
 * Calcula métricas de tickets agrupadas por statusCategory
 */
export function calculateTicketsMetrics(tickets: PopulatedTicket[]): TicketsMetrics {
  const metrics: TicketsMetrics = {
    total: tickets.length,
    byCategory: {},
    slaBreached: 0,
    avgResponseTime: 0,
    avgResolutionTime: 0,
    priorityCounts: {
      low: 0,
      medium: 0,
      high: 0,
      urgent: 0
    }
  };

  let totalResponseTime = 0;
  let responseTimeCount = 0;
  let totalResolutionTime = 0;
  let resolutionTimeCount = 0;

  tickets.forEach(ticket => {
    // Contar por statusCategory
    const cat = ticket.statusCategory || 'new';
    metrics.byCategory[cat] = (metrics.byCategory[cat] || 0) + 1;

    // SLA violado
    if (ticket.slaBreached) {
      metrics.slaBreached++;
    }

    // Contar por prioridade
    if (ticket.priority in metrics.priorityCounts) {
      metrics.priorityCounts[ticket.priority as keyof typeof metrics.priorityCounts]++;
    }

    // Tempo de resposta
    if (ticket.responseTime) {
      totalResponseTime += ticket.responseTime;
      responseTimeCount++;
    }

    // Tempo de resolução
    if (ticket.resolutionTime) {
      totalResolutionTime += ticket.resolutionTime;
      resolutionTimeCount++;
    }
  });

  // Calcular médias
  if (responseTimeCount > 0) {
    metrics.avgResponseTime = Math.round(totalResponseTime / responseTimeCount);
  }

  if (resolutionTimeCount > 0) {
    metrics.avgResolutionTime = Math.round(totalResolutionTime / resolutionTimeCount);
  }

  return metrics;
}

/**
 * Filtra tickets por busca de texto
 */
export function filterTicketsBySearch(
  tickets: PopulatedTicket[],
  searchTerm: string
): PopulatedTicket[] {
  if (!searchTerm.trim()) return tickets;

  const term = searchTerm.toLowerCase();
  return tickets.filter(ticket =>
    ticket.title?.toLowerCase().includes(term) ||
    ticket.ticketNumber?.toLowerCase().includes(term) ||
    ticket.description?.toLowerCase().includes(term) ||
    ticket.contact?.name?.toLowerCase().includes(term) ||
    ticket.category?.toLowerCase().includes(term) ||
    ticket.tags?.some(tag => tag.toLowerCase().includes(term))
  );
}
